import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useRef,
  useEffect,
  useState,
} from 'react';

import {
  Analytics,
  ActionSubjectIdType,
  SourceType,
} from '@trello/atlassian-analytics';
import { Button } from '@trello/nachos/button';
import { forTemplate } from '@trello/i18n';
import { HeaderTestIds } from '@trello/test-ids';
import { useAnalyticsAttributes } from './AnalyticsAttributesProvider';
import { ChevronDown } from './icons/ChevronDown';
import styles from './Tabs.less';
import { TabId } from './TabId';

const format = forTemplate('logged_out_header');
interface TabLinkProps {
  href?: string;
  index: TabId;
  label?: string;
  analyticsLinkName: ActionSubjectIdType;
  analyticsSource: SourceType;
}

const TabLink: React.FunctionComponent<TabLinkProps> = ({
  href,
  label,
  analyticsLinkName,
  analyticsSource,
}) => {
  const analyticsAttributes = useAnalyticsAttributes();

  const onClick = useCallback(
    () =>
      Analytics.sendClickedLinkEvent({
        linkName: analyticsLinkName,
        source: analyticsSource,
        attributes: {
          ...analyticsAttributes,
          event: 'clicked',
          eventComponent: 'globalNavLink',
          label,
          href,
          isMarketingEvent: true,
        },
      }),
    [analyticsAttributes, href, label, analyticsLinkName, analyticsSource],
  );

  return (
    <a className={styles.tabLink} href={href} onClick={onClick}>
      {label}
    </a>
  );
};

interface TabButtonProps {
  activeTabRef: React.RefObject<HTMLButtonElement>;
  index: TabId;
  isActive: boolean;
  setActiveTab: Dispatch<SetStateAction<number | undefined>>;
  title: string;
}

const TabButton: React.FunctionComponent<TabButtonProps> = ({
  activeTabRef,
  index,
  isActive,
  setActiveTab,
  title,
}) => {
  const analyticsAttributes = useAnalyticsAttributes();

  const onClickButton = useCallback(
    (e: React.MouseEvent) => {
      if (isActive) {
        setActiveTab(undefined);
      } else {
        const indexList: Array<ActionSubjectIdType> = [
          'featuresButton',
          'solutionsButton',
          'plansButton',
          'pricingLink',
          'resourcesButton',
        ];
        Analytics.sendClickedButtonEvent({
          buttonName: indexList[index],
          source: 'anonymousHeader',
          attributes: {
            ...analyticsAttributes,
            event: 'clicked',
            eventComponent: 'globalNavTab',
            label: title,
            isMarketingEvent: true,
          },
        }),
          setActiveTab(index);
      }
      e.stopPropagation();
    },
    [analyticsAttributes, index, isActive, setActiveTab, title],
  );

  return (
    <Button
      appearance="subtle"
      iconAfter={<ChevronDown />}
      aria-expanded={isActive}
      className={styles.tabButton}
      data-active={isActive}
      data-test-id={HeaderTestIds.LoggedOutHeaderWideTab}
      onClick={onClickButton}
      ref={isActive ? activeTabRef : null}
    >
      {title}
    </Button>
  );
};

interface TabsProps {
  activeTab?: number;
  setActiveTab: Dispatch<SetStateAction<number | undefined>>;
}

export const Tabs: React.FunctionComponent<TabsProps> = ({
  activeTab,
  setActiveTab,
}) => {
  const activeTabRef = useRef<HTMLButtonElement>(null);
  const [activeTabStyle, setActiveTabStyle] = useState<{
    left?: number;
    width: number;
  }>({ width: 0 });

  useEffect(() => {
    if (activeTabRef.current) {
      setActiveTabStyle({
        left: activeTabRef.current.offsetLeft + 14,
        width: activeTabRef.current.clientWidth - 28,
      });
    } else if (activeTabStyle && activeTabStyle.left) {
      setActiveTabStyle({
        left: activeTabStyle.left,
        width: 0,
      });
      setTimeout(() => {
        setActiveTabStyle({ width: 0 });
      }, 300);
    }
    // While activeTab isn't referenced in the effect, it is a valid value
    // to trigger a re-run of it. The ref essentially shadows & is controlled
    // by activeTab, but refs don’t work as hook dependencies, so disabling the rule.
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [activeTab, activeTabRef]);

  return (
    <div className={styles.tabGroup}>
      <TabButton
        activeTabRef={activeTabRef}
        index={TabId.Features}
        isActive={activeTab === TabId.Features}
        setActiveTab={setActiveTab}
        title={format('features-tab-label')}
      />
      <TabButton
        activeTabRef={activeTabRef}
        index={TabId.Solutions}
        isActive={activeTab === TabId.Solutions}
        setActiveTab={setActiveTab}
        title={format('solutions-tab-label')}
      />
      <TabButton
        activeTabRef={activeTabRef}
        index={TabId.Plans}
        isActive={activeTab === TabId.Plans}
        setActiveTab={setActiveTab}
        title={format('plans-tab-label')}
      />
      <TabLink
        href="/pricing"
        index={TabId.Pricing}
        label={format('pricing-tab-label')}
        analyticsLinkName="pricingLink"
        analyticsSource="anonymousHeader"
      />
      <TabButton
        activeTabRef={activeTabRef}
        index={TabId.Resources}
        isActive={activeTab === TabId.Resources}
        setActiveTab={setActiveTab}
        title={format('resources-tab-label')}
      />
      <div className={styles.underline} style={activeTabStyle} />
    </div>
  );
};
